import { useRef, useCallback } from 'react'

import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Circle,
  Text,
  Portal,
} from '@chakra-ui/react'
import { isMobileOnly as isSP } from 'react-device-detect'
import { Menu as MenuIcon } from 'src/components/icon'
import { useModal } from 'src/hooks/use-modal'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'

import {
  DeviceSettingsContent,
  type DeviceSettingsRef,
} from './DeviceSettingsContent'
import { useLocalPlayer } from '../../context/localPlayerContext'
import { useVideoCallRoom } from '../../context/videoCallRoomContext'
import {
  useMediaDevices,
  type MediaDeviceInfo,
} from '../../hooks/useMediaDevices'
import {
  ControlButtonType,
  getButtonSize,
  getIconSize,
} from '../../utils/controlButtonUtil'

type Props = {
  buttonType?: ControlButtonType
  colorScheme?: 'blue' | 'green'
}

export const VideoCallMenu: React.FC<Props> = ({
  buttonType = 'normal',
  colorScheme = 'blue',
}) => {
  const { leave: leaveRoom } = useVideoCallRoom()
  const { showModal } = useModal()
  const toast = useMirohaToast()
  const deviceSettingsRef = useRef<DeviceSettingsRef>(null)
  const { cameraDevices, microphoneDevices, isLoading } = useMediaDevices()
  const {
    switchCamera,
    switchMicrophone,
    currentCameraDeviceId,
    currentMicrophoneDeviceId,
  } = useLocalPlayer()

  const getDeviceName = useCallback(
    (deviceId: string, devices: MediaDeviceInfo[]) => {
      const device = devices.find(d => d.deviceId === deviceId)
      return device?.label || 'Unknown Device'
    },
    [],
  )

  const handleDeviceApply = useCallback(
    async (selectedCameraId: string, selectedMicrophoneId: string) => {
      try {
        const changedDevices: { type: 'カメラ' | 'マイク'; name: string }[] = []

        if (selectedCameraId && selectedCameraId !== currentCameraDeviceId) {
          await switchCamera(selectedCameraId)
          const cameraName = getDeviceName(selectedCameraId, cameraDevices)
          changedDevices.push({ type: 'カメラ', name: cameraName })
        }

        if (
          selectedMicrophoneId &&
          selectedMicrophoneId !== currentMicrophoneDeviceId
        ) {
          await switchMicrophone(selectedMicrophoneId)
          const microphoneName = getDeviceName(
            selectedMicrophoneId,
            microphoneDevices,
          )
          changedDevices.push({ type: 'マイク', name: microphoneName })
        }

        if (changedDevices.length > 0) {
          if (changedDevices.length === 1) {
            const device = changedDevices[0]
            toast({
              title: `${device.type}を ${device.name} に切り替えました`,
              status: 'success',
            })
          } else {
            toast({
              title: 'カメラ・マイクを切り替えました',
              description: (
                <>
                  {changedDevices.map((device, index) => (
                    <div key={index}>
                      {device.type}: {device.name}
                    </div>
                  ))}
                </>
              ),
              status: 'success',
            })
          }
        }
      } catch (error) {
        toast({
          title: 'カメラ・マイクの切り替えに失敗しました',
          description:
            error instanceof Error
              ? error.message
              : '不明なエラーが発生しました',
          status: 'error',
        })
        throw error
      }
    },
    [
      currentCameraDeviceId,
      currentMicrophoneDeviceId,
      switchCamera,
      switchMicrophone,
      toast,
      getDeviceName,
      cameraDevices,
      microphoneDevices,
    ],
  )

  const handleOpenDeviceSettings = () => {
    showModal({
      title: 'マイクとカメラの設定',
      submitButtonColor: colorScheme,
      size: '2xl',
      isSP,
      content: (
        <DeviceSettingsContent
          ref={deviceSettingsRef}
          cameraDevices={cameraDevices}
          microphoneDevices={microphoneDevices}
          isLoading={isLoading}
          currentCameraDeviceId={currentCameraDeviceId}
          currentMicrophoneDeviceId={currentMicrophoneDeviceId}
          onApply={handleDeviceApply}
        />
      ),
      submitText: '設定する',
      cancelText: 'キャンセル',
      onSubmit: async () => {
        if (deviceSettingsRef.current) {
          await deviceSettingsRef.current.handleApply()
        }
      },
    })
  }

  const handleReloadVideoCall = () => {
    showModal({
      title: 'ビデオ通話を再接続しますか？',
      submitButtonColor: colorScheme,
      size: '3xl',
      isSP,
      content: (
        <Text>
          通話に不具合が発生している場合、再接続することで解決する場合があります。
          <br />
          ※ビデオ通話に問題がない場合は再接続しないでください。
        </Text>
      ),
      submitText: '再接続する',
      cancelText: '今はしない',
      onSubmit: () => {
        leaveRoom()
        window.location.reload()
      },
    })
  }

  const buttonSize = getButtonSize(buttonType)
  const iconSize = getIconSize(buttonType)

  return (
    <Menu>
      <MenuButton
        w="max-content"
        _hover={{
          '.menu-button': {
            bg: 'gray.50',
          },
        }}
        _active={{
          '.menu-button': {
            bg: 'gray.100',
          },
        }}
        _focusVisible={{
          '.menu-button': {
            boxShadow: 'outline',
          },
        }}
      >
        <Circle
          as="span"
          pos="relative"
          className="menu-button"
          cursor="pointer"
          borderColor={colorScheme === 'blue' ? 'blue.500' : 'green.500'}
          bg="white"
          color={colorScheme === 'blue' ? 'blue.500' : 'green.500'}
          size={buttonSize}
          borderWidth="2px"
        >
          <MenuIcon size={iconSize} />
        </Circle>
      </MenuButton>
      <Portal>
        <MenuList>
          <MenuItem color="gray.800" onClick={handleOpenDeviceSettings}>
            マイクとカメラの設定
          </MenuItem>
          <MenuItem color="gray.800" onClick={handleReloadVideoCall}>
            ビデオ通話の再接続
          </MenuItem>
        </MenuList>
      </Portal>
    </Menu>
  )
}
